import React, { useState } from "react";
import { ApolloError } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { Flex, Text, toast } from "@jsq/ds/core";
import { Form, FormTextInput, SubmitButton, useFormContext } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useCreateOnboardingJobStepMutation } from "~/graphql/supergraphHooks";
import {
  WorkflowStep,
  DataStudioEnvironment,
  DATA_STUDIO_DASHBOARD_ROUTE,
} from "../../constants";
import { useCrmImportJobDetails } from "../../hooks/useCrmImportQueries";
import { useIdQueryParam } from "../../hooks/useIdQueryParam";
import { getWorkflowRoute } from "../../utils/workflowUtils";
import LoadingOverlay from "../LoadingOverlay";
import * as styles from "./styles_vanilla.css";
import { type ProjectSetupFormData } from "./types";
import { validateForm } from "./utils";

// Form content component that can access form context
const ProjectSetupFormContent: React.FC<{
  isSubmitting: boolean;
  isViewingExistingJob: boolean;
}> = ({ isSubmitting, isViewingExistingJob }) => {
  const { values, errors } = useFormContext<ProjectSetupFormData>();

  // Check if all required fields are filled
  const allFieldsFilled = Boolean(
    values.arenaName?.trim() && values.arenaUserEmail?.trim() && values.jobName?.trim(),
  );

  // Check if there are any validation errors
  const hasErrors = Boolean(errors.arenaName || errors.arenaUserEmail || errors.jobName);

  // Button is enabled only when all fields are filled AND there are no errors AND not viewing existing job
  const isFormValid = allFieldsFilled && !hasErrors && !isViewingExistingJob;

  // Calculate title and button text outside of render
  const buttonTitle = isViewingExistingJob
    ? "Job already exists - proceed to next step"
    : !isFormValid
      ? "Please fill all fields with valid information"
      : "";

  const buttonText = isSubmitting
    ? "Creating..."
    : isViewingExistingJob
      ? "Next: Import Data"
      : "Next: Import Data";

  return (
    <>
      <Flex flexDirection="column" gap="20px" className={styles.formContainer}>
        {/* Arena Domain Name */}
        <FormTextInput
          name="arenaName"
          label="Arena domain name"
          placeholder="Enter arena domain name (no spaces)..."
          className={styles.textInput}
          required
          disabled={isViewingExistingJob}
        />

        {/* Arena User Email */}
        <FormTextInput
          name="arenaUserEmail"
          label="Arena user email"
          placeholder="e.g., user@company.com"
          className={styles.textInput}
          required
          disabled={isViewingExistingJob}
        />

        {/* Job Name */}
        <FormTextInput
          name="jobName"
          label="Job Name"
          placeholder="Enter job or project name..."
          className={styles.textInput}
          required
          disabled={isViewingExistingJob}
        />
      </Flex>

      {/* Action Button */}
      <Flex justifyContent="flex-end" className={styles.actionContainer}>
        <SubmitButton
          className={styles.nextButton}
          variant="custom"
          backgroundColor={Colors.Neutral900}
          textColor={Colors.Neutral0}
          disabled={isSubmitting || !isFormValid}
          title={buttonTitle}
        >
          {buttonText}
        </SubmitButton>
      </Flex>
    </>
  );
};

const ProjectSetup: React.FC = () => {
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { idFromUrl, createdFromUrl, getQueryParamsWithId } = useIdQueryParam();

  // Fetch job details if ID is present in URL
  const {
    data: jobDetails,
    loading: jobLoading,
    error: jobError,
    refetch: refetchJobDetails,
  } = useCrmImportJobDetails(idFromUrl || "", {
    skip: !idFromUrl,
  });

  // Determine if we're viewing an existing job
  const isViewingExistingJob = Boolean(idFromUrl && jobDetails);

  const [createJob] = useCreateOnboardingJobStepMutation({
    onCompleted: (data) => {
      toast.success("Project setup completed successfully!");
      // Navigate to next step with job ID (either from URL or newly created)
      const jobId = idFromUrl || data.dataStudioCreateJob?.id;
      const createdAt = createdFromUrl || data.dataStudioCreateJob?.createdAt;

      const queryParams = jobId
        ? {
            Id: jobId,
            ...(createdAt && { created: createdAt }),
          }
        : getQueryParamsWithId();

      history.push(getWorkflowRoute(WorkflowStep.DATA_IMPORT, queryParams));
    },
    onError: (error: ApolloError) => {
      toast.error(`Failed to create job: ${error.message}`);
    },
  });

  const handleSubmit = async (values: Partial<ProjectSetupFormData>) => {
    // If viewing existing job, skip creation and navigate directly to next step
    if (isViewingExistingJob) {
      const queryParams = {
        Id: idFromUrl!,
        ...(createdFromUrl && { created: createdFromUrl }),
      };
      history.push(getWorkflowRoute(WorkflowStep.DATA_IMPORT, queryParams));
      return;
    }

    if (!values.arenaName || !values.arenaUserEmail || !values.jobName) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await createJob({
        variables: {
          input: {
            jobType: "CRM_IMPORT",
            arenaDomain: values.arenaName.toLowerCase(),
            arenaUserEmail: values.arenaUserEmail.toLowerCase(),
            env: DataStudioEnvironment.TEST, // CRM import always uses test environment
            jobConfig: {
              tools: [], // Empty tools array for PROJECT_SETUP state
            },
            jobName: values.jobName,
            substate: "PROJECT_SETUP",
            ...(createdFromUrl && { createdAt: createdFromUrl }),
          },
        },
      });
    } catch {
      // Error handling is done in onError callback
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dynamic initial values based on job data
  const initialValues: ProjectSetupFormData = {
    arenaName: jobDetails?.arenaDomain || "",
    arenaUserEmail: jobDetails?.arenaUserEmail || "",
    jobName: jobDetails?.jobName || "",
  };

  // Show loading state when fetching job details
  if (jobLoading && !!idFromUrl) {
    return (
      <LoadingOverlay
        loading={true}
        message="Loading job details..."
        onRetry={() => refetchJobDetails()}
        onCancel={() => history.push(DATA_STUDIO_DASHBOARD_ROUTE)}
        cancelText="Back to Dashboard"
      >
        <div className={styles.container}>{/* Empty container for loading state */}</div>
      </LoadingOverlay>
    );
  }

  // Show error state if job fetch failed
  if (jobError && idFromUrl) {
    return (
      <LoadingOverlay
        error={jobError}
        message="Loading job details..."
        onRetry={() => refetchJobDetails()}
        onCancel={() => history.push(DATA_STUDIO_DASHBOARD_ROUTE)}
        cancelText="Back to Dashboard"
      >
        <div className={styles.container}>{/* Empty container for error state */}</div>
      </LoadingOverlay>
    );
  }

  // Show the actual form once loading is complete or no job ID
  return (
    <div className={styles.container}>
      <Flex flexDirection="column" gap="24px">
        {/* Header */}
        <Flex alignItems="center" gap="12px" className={styles.header}>
          <Flex className={styles.iconContainer} alignItems="center" justifyContent="center">
            <Icon type="building" size="medium" color={Colors.Neutral0} />
          </Flex>
          <Flex flexDirection="column">
            <Text variant="h4" color={Colors.Neutral900}>
              Project Setup
            </Text>
            <Text variant="regular-md" color={Colors.Neutral600}>
              {isViewingExistingJob
                ? "Review the project information for this existing import job."
                : "Configure the basic project information before starting the import process."}
            </Text>
          </Flex>
        </Flex>

        {/* Form */}
        <Form initialValues={initialValues} onSubmit={handleSubmit} validate={validateForm}>
          <ProjectSetupFormContent
            isSubmitting={isSubmitting}
            isViewingExistingJob={isViewingExistingJob}
          />
        </Form>
      </Flex>
    </div>
  );
};

export default ProjectSetup;
